import React, { useEffect, useRef, useState } from "react";
import "./Testimonials.css";
import Lottie from "lottie-react";
import google_g_logo from "../assets/google_g_logo.png";
import spinningWheel from "../assets/spinningWheel.json";
import { useTranslation } from "react-i18next";

const formatDate = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24 * 30));
  return diff === 0 ? "Just now" : `${diff} month${diff > 1 ? "s" : ""} ago`;
};

const Testimonials = () => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(null);
  const [count, setCount] = useState(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`/.netlify/functions/get-google-reviews?lang=${i18n.language}`)
      .then((res) => res.json())
      .then((data) => {
        setReviews(data.reviews || []);
        setRating(data.rating);
        setCount(data.count);
      })
      .catch(console.error);
  }, [i18n.language]);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setIsAtStart(el.scrollLeft <= 0);
    setIsAtEnd(el.scrollLeft + el.offsetWidth >= el.scrollWidth - 1);
  };

  useEffect(() => {
  const el = scrollRef.current;
  if (!el) return;

  const updateOnceReady = () => {
    updateScrollState();
  };

  // Update state after all DOM is painted
  window.addEventListener("load", updateOnceReady);
  setTimeout(updateScrollState, 300); // Fallback if load doesn't fire (e.g. SPA)

  el.addEventListener("scroll", updateScrollState);
  return () => {
    window.removeEventListener("load", updateOnceReady);
    el.removeEventListener("scroll", updateScrollState);
  };
}, []);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const card = el.querySelector(".testimonial-card");
    if (!card) return;

    const cardWidth = card.offsetWidth;
    const gap = parseFloat(getComputedStyle(el.firstChild).gap) || 24;
    const scrollAmount = cardWidth + gap;

    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <section className="testimonials-section">
      <div className="container">
        <div className="testimonials-header px-0 px-md-3">
          <div className="header-row">
            <Lottie animationData={spinningWheel} className="spinning-wheel" />
            <h2>{t("testimonials.header")}</h2>
          </div>
          <p>{t("testimonials.intro")}</p>
        </div>

        <div className="testimonial-carousel">
          <button
            className={`carousel-arrow left ${isAtStart ? "disabled" : ""}`}
            onClick={() => scroll("left")}
            disabled={isAtStart}
          >
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="testimonial-scroll-wrapper" ref={scrollRef}>
            <div className="testimonial-cards">
              {reviews.slice(0, 5).map((review, i) => {
                const initial = review.author_name?.[0] || "?";
                const color = ["#AD2C48", "#6844A7", "#2A7D78"][i % 3];

                return (
                  <a
                    href="https://www.google.com/maps/place/?q=place_id=ChIJpSAOQ-jmd2oRq6zlraOXXMc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="testimonial-card"
                    key={i}
                  >
                    <span className="quote-mark" style={{ color }}>“</span>
                    <div className="testimonial-header">
                      {review.profile_photo_url ? (
                        <img src={review.profile_photo_url} alt={review.author_name} className="avatar" />
                      ) : (
                        <div className="avatar placeholder">{initial}</div>
                      )}
                      <div className="name">{review.author_name}</div>
                      <div className="stars">{"★".repeat(review.rating)}</div>
                    </div>
                    <p className="text">
                      {review.text.length > 200
                        ? `${review.text.slice(0, 200)}...`
                        : review.text}
                    </p>
                    <div className="date">{formatDate(review.time)}</div>
                  </a>
                );
              })}
            </div>
          </div>

          <button
            className={`carousel-arrow right ${isAtEnd ? "disabled" : ""}`}
            onClick={() => scroll("right")}
            disabled={isAtEnd}
          >
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {rating && (
          <div className="testimonials-footer">
            <div className="testimonials-footer-left">
              <img src={google_g_logo} alt="Google" />
              <span className="rating">{rating.toFixed(1)}</span>
              <span className="count">
                {count} {t("testimonials.reviews")}
              </span>
            </div>

            <a
              href="https://search.google.com/local/reviews?placeid=ChIJkeQ4W6qUNCwRCEdthnNtg-g"
              target="_blank"
              rel="noopener noreferrer"
              className="view-all"
            >
              <svg className="arrow-icon-testimonials" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L12 22M12 22L5 15M12 22L19 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t("testimonials.viewAll")}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
